import { useMemo } from 'react';

import { Dimensions, Metrics, Sources } from 'components/insights/constants';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import useReferralAnalytics, {
  ReferralAnalyticsQueryParams,
} from 'components/insights/referrals/query/useReferralAnalytics';
import { getSlideoutEntityFilter, useReferralsInsightsStore } from 'stores/referralsInsightsStore';

import { useReferralsInsightsContext } from '../ReferralsContainer';

// Queries that back the slideout shell's subtitle counts and the metric-drill top-by cards.

const RATE_METRICS = [
  Metrics.REVIEW_ID_COUNT,
  Metrics.RESPONSE_TIME_SECONDS_AVERAGE,
  Metrics.ACCEPTANCE_RATE,
  Metrics.DENIAL_RATE,
];

const QUERY_A_PARAMS: ReferralAnalyticsQueryParams = {
  source: Sources.INSIGHTS_REFERRALS,
  dimensions: [`${Dimensions.RECEIVING_GROUP}:with_id`, Dimensions.SENDING_GROUP],
  metrics: RATE_METRICS,
  rollups: true,
};

const QUERY_B_PARAMS: ReferralAnalyticsQueryParams = {
  source: Sources.INSIGHTS_REFERRALS,
  dimensions: [`${Dimensions.RECEIVING_CLIENT}:with_id`, Dimensions.HEALTH_PLAN_CLASSIFICATION],
  metrics: RATE_METRICS,
  rollups: true,
};

// SQL ROLLUP can't emit a "plan preserved, client nulled" row from Query B (right-to-left only),
// so plan top-by comes from this single-dim query.
const QUERY_C_PARAMS: ReferralAnalyticsQueryParams = {
  source: Sources.INSIGHTS_REFERRALS,
  dimensions: [Dimensions.HEALTH_PLAN_CLASSIFICATION],
  metrics: RATE_METRICS,
};

export type TopEntity = { name: string; value: number } | null;

export type SlideoutSharedData = {
  sendingCount: number;
  receivingCount: number;
  fastestResponder: TopEntity;
  slowestResponder: TopEntity;
  topAcceptingReceivingClient: TopEntity;
  topDenyingReceivingClient: TopEntity;
  topAcceptingPlan: TopEntity;
  topDenyingPlan: TopEntity;
  countsLoading: boolean;
  topByLoading: boolean;
  partnerLabel: string;
};

export default function useSlideoutSharedData(): SlideoutSharedData {
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const { sendingGroupType } = useReferralsInsightsContext();
  const partnerLabel = selectedReceivingGroupType?.displayName || sendingGroupType?.displayName || 'Partner';
  const extraFilters = useMemo(
    () => (slideoutContext ? getSlideoutEntityFilter(slideoutContext) : null),
    [slideoutContext]
  );

  // Query B only powers the rate slideouts' top-by cards. Skip it for entity drills and for the
  // response-time metric drill — keeps the entity slideout from paying for unused data.
  const needsTopBy =
    slideoutContext?.kind === 'metric' &&
    (slideoutContext.metric === 'denial_rate' || slideoutContext.metric === 'acceptance_rate');

  const {
    rollups: rollupsA,
    data: dataA,
    loading: countsLoading,
  } = useReferralAnalytics(QUERY_A_PARAMS, {
    enabled: !!slideoutContext,
    extraFilters,
    queryKey: 'referral-slideout-shared-a',
  });

  const { rollups: rollupsB, loading: topByLoading } = useReferralAnalytics(QUERY_B_PARAMS, {
    enabled: needsTopBy,
    extraFilters,
    queryKey: 'referral-slideout-shared-b',
  });

  const { data: dataC } = useReferralAnalytics(QUERY_C_PARAMS, {
    enabled: needsTopBy,
    extraFilters,
    queryKey: 'referral-slideout-shared-c',
  });

  return useMemo(() => {
    const perReceiverA = findGrouping(rollupsA, 1);
    const groupingB1 = findGrouping(rollupsB, 1);

    const sendingCount = countDistinct(dataA, Dimensions.SENDING_GROUP);
    const receivingCount = countDistinct(dataA, Dimensions.RECEIVING_GROUP);

    const { fastest, slowest } = pickFastestSlowest(perReceiverA);

    return {
      sendingCount,
      receivingCount,
      fastestResponder: fastest,
      slowestResponder: slowest,
      topAcceptingReceivingClient: topByMetric(groupingB1, Dimensions.RECEIVING_CLIENT, Metrics.ACCEPTANCE_RATE),
      topDenyingReceivingClient: topByMetric(groupingB1, Dimensions.RECEIVING_CLIENT, Metrics.DENIAL_RATE),
      topAcceptingPlan: topByMetric(dataC, Dimensions.HEALTH_PLAN_CLASSIFICATION, Metrics.ACCEPTANCE_RATE),
      topDenyingPlan: topByMetric(dataC, Dimensions.HEALTH_PLAN_CLASSIFICATION, Metrics.DENIAL_RATE),
      countsLoading,
      topByLoading,
      partnerLabel,
    };
  }, [rollupsA, dataA, rollupsB, dataC, countsLoading, topByLoading, partnerLabel]);
}

// Fetches a single-dimension breakdown for the currently open slideout (gated on slideoutContext).
// Returns the raw name/count arrays; callers do their own filter/transform/sort.
export function useSlideoutDimensionCounts(
  dimension: Dimensions,
  queryKey: string,
  options: { enabled?: boolean } = {}
): { names: ReadonlyArray<string>; counts: ReadonlyArray<number | null>; loading: boolean } {
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);
  const { enabled = true } = options;
  const params = useMemo<ReferralAnalyticsQueryParams>(
    () => ({
      source: Sources.INSIGHTS_REFERRALS,
      dimensions: [dimension],
      metrics: [Metrics.REVIEW_ID_COUNT],
    }),
    [dimension]
  );
  const { data, loading } = useReferralAnalytics(params, {
    enabled: !!slideoutContext && enabled,
    queryKey,
  });
  return {
    names: data.dimensions.getCurrentDimensionValuesByIndex(0),
    counts: data.metrics.getCurrentSeries(Metrics.REVIEW_ID_COUNT),
    loading,
  };
}

type Grouping = AnalyticsResponseRollupCollection['groupings'][number];
type Source = Grouping | AnalyticsResponseData;

function findGrouping(rollups: AnalyticsResponseRollupCollection, value: number): Grouping | undefined {
  return rollups.groupings.find((g) => g.current[0]?.grouping === value);
}

function dimIndex(source: Source, dimension: Dimensions): number {
  return source.analyticsResponse.findDimensionIndex(dimension);
}

function countDistinct(source: Source | undefined, dimension: Dimensions): number {
  if (!source) return 0;
  const idx = dimIndex(source, dimension);
  if (idx < 0) return 0;
  const values = source.dimensions.getCurrentDimensionValuesByIndex(idx);
  return new Set(values.filter((v) => v != null && v !== 'None')).size;
}

function pickFastestSlowest(g: Grouping | undefined): { fastest: TopEntity; slowest: TopEntity } {
  if (!g) return { fastest: null, slowest: null };
  const idx = dimIndex(g, Dimensions.RECEIVING_GROUP);
  if (idx < 0) return { fastest: null, slowest: null };
  const names = g.dimensions.getCurrentDimensionValuesByIndex(idx);
  const times = g.metrics.getCurrentSeries(Metrics.RESPONSE_TIME_SECONDS_AVERAGE);
  const rows = names
    .map((name, i) => ({ name, value: times[i] }))
    .filter((r) => r.name && r.name !== 'None' && r.value != null && r.value !== -1) as Array<{
    name: string;
    value: number;
  }>;
  if (rows.length === 0) return { fastest: null, slowest: null };
  const sorted = [...rows].sort((a, b) => a.value - b.value);
  return { fastest: sorted[0], slowest: sorted[sorted.length - 1] };
}

function topByMetric(source: Source | undefined, dimension: Dimensions, metric: Metrics): TopEntity {
  if (!source) return null;
  const idx = dimIndex(source, dimension);
  if (idx < 0) return null;
  const names = source.dimensions.getCurrentDimensionValuesByIndex(idx);
  const values = source.metrics.getCurrentSeries(metric);
  const rows = names
    .map((name, i) => ({ name, value: values[i] }))
    .filter((r) => r.name && r.name !== 'None' && r.value != null && r.value !== -1) as Array<{
    name: string;
    value: number;
  }>;
  if (rows.length === 0) return null;
  rows.sort((a, b) => b.value - a.value);
  return rows[0];
}
